import React, { useContext, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  params,
  extend,
  string,
  bool,
  arrayOf,
  number,
  shape,
  useLazyDataModel,
  customType,
} from '@thd-nucleus/data-sources';
import { Alert, Link, Typography } from '@one-thd/sui-atomic-components';
import { useStore } from '@thd-nucleus/experience-context';
import { localizeToProject, triggerAnalytics } from './helpers';
import { 
  PROJECT_ADD_DELIVERY_ZIPCODE_MESSAGING_ERROR, 
  PROJECT_SHOPPING_FOR_ERROR,
  SHOP_LOCATION_LINK_CLICK } from './analytics';
import THDStorageUtils, { SESSION_STORAGE } from '@thd-olt-global/thd-storage-utils';
import { DeliveryZipcodeDrawer } from './DeliveryZipcodeDrawer';


export const LocalizationBanners = ({ 
  projectId,
  projectName,
  assortmentZip,
  addresses,
  preferredStore,
  autoAddstore, 
  onLocalizationUpdate,
  removeDrawerLocalStorageValues
}) => {
  const shopThisLocation = 'SHOPTHISLOCATION';
  const noAssortmentZip = 'NOASSORTMENTZIP';
  const nowShoppingFor = 'NOWSHOPPINGFOR';

  const assortmentZipId = addresses?.find((address) => address.addressType === 'ASSORTMENT_ZIP')?.id || null;

  const closeBanner = () => {
      setBanner(null);
  };

  const [banner, setBanner] = useState(null);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);

  const store = useStore();
  const { storeId } = store;

  const getDeliveryZip = () => {
    let zipCode = '';
    try {
      if (typeof window !== 'undefined') {
        const zipDetails = window?.THD_LOCALIZER_AUTO_INIT?.Localizer?.getDeliveryZipcodeDetails();
        zipCode = zipDetails?.zipcode;
      }
    } catch (err) {
    // eslint-disable-next-line no-console
      console.error(err);
    }
    return zipCode;
  };

  const doesPreferredStoreMatchCurrentStore = ({ storeId: currentStoreId, preferredStore = '' }) => {
    if (preferredStore[0] === '0') {
      const preferredStoreWithoutLeadingZero = preferredStore.substring(1);
      return currentStoreId === preferredStoreWithoutLeadingZero;
    }
    return currentStoreId === preferredStore;
  };

  const doesProjectLocalizationNotMatchCurrentLocalization = () => {
   return !doesPreferredStoreMatchCurrentStore({ storeId, preferredStore }) || getDeliveryZip() !== assortmentZip
  }

  const onShopThisProject = () => {
    localizeToProject({
      assortmentZip,
      preferredStore,
      analyticsString: SHOP_LOCATION_LINK_CLICK,
      onUpdate: onLocalizationUpdate
    });
  };

  const [fetchStores, { data: storesData, error: storesError, storesResponseLoading }] = 
  useLazyDataModel('storeSearch', {
    variables: {
      pagesize: '1'
    }
  });

  const [updateProjectDetails, updateProjectResponse] 
  = useLazyDataModel('updateProject', {
    variables: {
      projectId
    },
    refetchQueries: [
      'projectDetails'
    ],
    context: { withAuth: true }
  });

  useEffect(() => {
    // if no assortment zip, show the noAssortmentZip banner
    if (!assortmentZip) {
      setBanner(noAssortmentZip);
      triggerAnalytics(PROJECT_ADD_DELIVERY_ZIPCODE_MESSAGING_ERROR, {});
    // if assortment zip is present, but preferred store is not set, automatically fetch stores if autoAddstore is true
    } else if (assortmentZip && !preferredStore) {
      if (!autoAddstore) { return ; };
      fetchStores({
        variables: {
          storeSearchInput: assortmentZip
        }
      });
    // if assortment zip is present, preferred store is set, and they don't match the current localization, show the shopThisLocation banner
    } else if (doesProjectLocalizationNotMatchCurrentLocalization()){
      setBanner(shopThisLocation);
      triggerAnalytics(PROJECT_SHOPPING_FOR_ERROR, {});
    // if assortment zip and preferred store are present, they match the current localization, check if the user just localized to the project
    } else if (!hasLoadedOnce) {
      const userJustLocalizedToProject = THDStorageUtils.get(SESSION_STORAGE, { key: 'localizedToProject' });
      const userJustLocalizedToProjectFromAddTo = THDStorageUtils.get(SESSION_STORAGE, { key: 'localizedToProjectFromAddTo' });
      setHasLoadedOnce(true);
      if (userJustLocalizedToProject && userJustLocalizedToProject !== 'undefined') {
        setBanner(nowShoppingFor);
        THDStorageUtils.remove(SESSION_STORAGE, { key: 'localizedToProject' });
      } else if (userJustLocalizedToProjectFromAddTo && userJustLocalizedToProjectFromAddTo !== 'undefined') {
        setBanner(nowShoppingFor);
        if (removeDrawerLocalStorageValues){
          THDStorageUtils.remove(SESSION_STORAGE, { key: 'localizedToProjectFromAddTo' });
        }
      } else {
        setBanner(null);
      }
    }
  }, [assortmentZip, preferredStore, storeId, autoAddstore]);

  useEffect(() => {
    const storeIdFromResponse = storesData?.storeSearch?.stores?.[0]?.storeId || null;
    if (storeIdFromResponse && !storesError && !storesResponseLoading) {
      updateProjectDetails({
        variables: {
          operationType: 'INFO',
          projectInfo: {
            projectName: projectName,
            preferredStore: storeIdFromResponse
          }
        }
      });
    }
  }, [storesData, storesError, storesResponseLoading]);

  if (!banner) {
      return null;
  };

  return (
    <>
      {banner === shopThisLocation && (
          <div className="sui-mb-4">
            <Alert status="info" size="large" onClose={closeBanner}>
              <div className="sui-flex sui-flex-col md:sui-flex-row">
                  <Typography variant="body-sm">
                    Shopping for {projectName}?&nbsp;
                  </Typography>
                  <Typography variant="body-sm">
                  {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                    <Link className="sui-text-sm sui-underline" onClick={onShopThisProject} component="button">
                        Shop This Location
                    </Link>
                  </Typography>
              </div>
            </Alert>
          </div>
      )}
      {banner === noAssortmentZip && (
        <div className="sui-pt-1 sui-mb-4">
            <Alert onClose={closeBanner} status="error" size="large">
              <div className="sui-flex sui-flex-col md:sui-flex-row sui-text-sm">
                <Typography variant="body-sm">
                  Shopping for {projectName}?&nbsp;
                </Typography>
                <Typography variant="body-sm">
                {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                <DeliveryZipcodeDrawer
                  assortmentZipCode={assortmentZip}
                  projectId={projectId}
                  assortmentZipId={assortmentZipId || ''}
                  onUpdate={onLocalizationUpdate}
                  preferredStore={preferredStore}
                  customActionButtonRenderFunction={({onClick}) => (
                    <Link
                      component="button"
                      className="sui-text-sm sui-underline"
                      onClick={onClick}
                    >
                      Add Delivery Zip Code
                    </Link>
                  )}
                />
                </Typography>
              </div>
            </Alert>
        </div>
      )}
      {banner === nowShoppingFor && (
          <div className="sui-pt-1 sui-pb-2">
            <Alert onClose={closeBanner} status="success" size="large">
              {`Now Shopping "${projectName}"`}
            </Alert>
          </div>
        )}
    </>
  );
};

LocalizationBanners.propTypes = {
  projectId: PropTypes.string,
  projectName: PropTypes.string,
  assortmentZip: PropTypes.string,
  addresses: PropTypes.arrayOf(
    PropTypes.shape({
      addressType: PropTypes.string,
      id: PropTypes.string
    })
  ),
  preferredStore: PropTypes.string,
  onLocalizationUpdate: PropTypes.func,
  autoAddstore: PropTypes.bool,
  removeDrawerLocalStorageValues: PropTypes.bool
};

LocalizationBanners.defaultProps = {
  projectId: '',
  projectName: '',
  assortmentZip: '',
  addresses: [],
  preferredStore: '',
  onLocalizationUpdate: () => {},
  autoAddstore: false,
  removeDrawerLocalStorageValues: false
};

LocalizationBanners.displayName = 'LocalizationBanners';

LocalizationBanners.dataModel = extend({
  updateProject: params({
    projectId: string().isRequired(),
    operationType: customType('OperationType').enum(['STATUS', 'INFO', 'ADDRESS']).isRequired(),
    projectInfo: customType('Info').shape({
      preferredStore: string(),
    }),
  }).mutation().shape({
    id: string()
  }),
  storeSearch: params({
    storeSearchInput: string().isRequired(),
    pagesize: string()
  }).shape({
    stores: arrayOf(
      shape({
        storeId: string(),
      })
    )
  })
});